/**
 * Tasbih — «Tasbih-innsamling» (owner 2026-11-02): the mosque opens a collection and members send the count from the
 * app's tasbih counter to it, with their name or anonymously. Counting itself stays on the phone; only a count the
 * member chooses to send reaches the mosque.
 *
 * Behind the `tasbih` capability (the menu item is absent without it, and every route answers 404) and tasbih.manage
 * (admin tier and the imam). The list shows each collection's zikr, progress towards the goal, how many counts were sent,
 * whether it is open and its last day; «Ny innsamling» opens one. «Tellinger» shows who sent what: the per-person totals
 * of counts sent «med navn» and every count, newest first («Anonym» when sent anonymously) — religious practice, so the
 * server logs each look (view_member, never a name). A wrong count is removed with two clicks. The rules (phrases,
 * limits, the check of a collection) are core dhikr.js through core.ts; the server checks again. Every call to the
 * /tasbih routes is in this file.
 */
import React, { useState } from 'react';
import { errSummary, errText, fieldErrors, post, send, type Body } from '../lib/api';
import { useLoad, useMsg, dropChanged } from '../lib/hooks';
import { longDate, osloTime, plural } from '../lib/format';
import { Body as CardBody, Btn, Card, Field, FieldError, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { Dhikr } from '../core';

const BASE = '/tasbih';
const put = (path: string, body: unknown) => send(path, { method: 'PUT', body: JSON.stringify(body) });
const del = (path: string) => send(path, { method: 'DELETE' });

/** The phrase as staff read it: its short name and the Norwegian meaning; null is every phrase. */
export function phraseName(id: string | null | undefined): string {
  if (!id) return 'Alle (fritt)';
  const p = Dhikr.phrase(id);
  return p ? `${p.name} — ${p.no}` : id;
}

/** «1 234 av 5 000 · 24 %», or just the total without a goal. Numbers in Norwegian grouping. */
export function progressText(c: { total?: number; target?: number | null; percent?: number | null }): string {
  const n = (v: number) => v.toLocaleString('nb-NO');
  const total = c.total || 0;
  return c.target ? `${n(total)} av ${n(c.target)} · ${c.percent ?? Math.min(100, Math.floor((total * 100) / c.target))} %` : `${n(total)} i alt`;
}

const REASON_NO: Record<string, string> = {
  'title:required': 'Skriv en tittel.', 'title:too_long': `Høyst ${Dhikr.LIMITS.title} tegn.`, 'phraseId:unknown': 'Velg fra listen.',
  'target:invalid': `Et helt tall fra 1 til ${Dhikr.LIMITS.target.toLocaleString('nb-NO')}.`, 'endsOn:invalid': 'Velg en dato.',
  'note:too_long': `Høyst ${Dhikr.LIMITS.note} tegn.`,
};

export const EMPTY = { title: '', phraseId: '', target: '', endsOn: '', note: '' };
export type Form = typeof EMPTY;
export const toForm = (c: Body): Form => ({ title: c.title || '', phraseId: c.phraseId || '', target: c.target ? String(c.target) : '',
  endsOn: c.endsOn || '', note: c.note || '' });
/** What the server is sent: the whole collection, empty as null; a goal typed with spaces («10 000») is a number. */
export const bodyOf = (f: Form) => ({ title: f.title.trim(), phraseId: f.phraseId || null,
  target: f.target.trim() ? Number(f.target.replace(/[\s.]/g, '')) : null, endsOn: f.endsOn || null, note: f.note.trim() || null });
/** core's check, in words under each field. Empty: fine. */
export function formErrors(f: Form): Record<string, string> {
  const v = Dhikr.checkCollection(bodyOf(f));
  if (v.ok) return {};
  const out: Record<string, string> = {};
  for (const e of v.errors) if (!out[e.field]) out[e.field] = REASON_NO[`${e.field}:${e.reason}`] || 'Ugyldig verdi.';
  return out;
}

function CollectionForm({ f, set, errors, phraseLocked }: { f: Form; set: (f: Form) => void; errors: Record<string, string>; phraseLocked: boolean }) {
  const err = (k: string) => <FieldError text={errors[k]} />;
  return (
    <>
      <div className="row">
        <Field label="Tittel" required className="field" note="For eksempel «Durood for fredag» eller «Zikr-kveld».">{(id, d) => <><input id={id} aria-describedby={d}
          maxLength={Dhikr.LIMITS.title} autoComplete="off" value={f.title} onChange={(e) => set({ ...f, title: e.target.value })} />{err('title')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Zikr" className="field half" note={phraseLocked ? 'Kan ikke endres: noen har sendt tellinger.' : 'Én zikr, eller alle.'}>{(id, d) => <><select id={id}
          aria-describedby={d} disabled={phraseLocked} value={f.phraseId} onChange={(e) => set({ ...f, phraseId: e.target.value })}>
          <option value="">Alle (fritt)</option>
          {Dhikr.PHRASES.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.no}</option>)}</select>{err('phraseId')}</>}</Field>
        <Field label="Mål (antall)" className="field half" note="Valgfritt. For eksempel 10000.">{(id, d) => <><input id={id} aria-describedby={d} inputMode="numeric"
          autoComplete="off" value={f.target} onChange={(e) => set({ ...f, target: e.target.value })} />{err('target')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Siste dag" className="field half" note="Valgfritt. Etter denne dagen tar innsamlingen ikke imot tellinger.">{(id, d) => <><input id={id} aria-describedby={d}
          type="date" value={f.endsOn} onChange={(e) => set({ ...f, endsOn: e.target.value })} />{err('endsOn')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Notat" className="field" note="Valgfritt. Vises i appen under tittelen.">{(id, d) => <><textarea id={id} aria-describedby={d} rows={2}
          maxLength={Dhikr.LIMITS.note} value={f.note} onChange={(e) => set({ ...f, note: e.target.value })} />{err('note')}</>}</Field>
      </div>
    </>
  );
}

/** «Tellinger» for one collection: per person (named counts), the anonymous sum, and every count with «Fjern». */
function Entries({ c, onChanged }: { c: Body; onChanged: () => void }) {
  const { data, error, reload } = useLoad<Body>(`${BASE}/collections/${c.id}/entries`, [c.id]);
  const [msg, say] = useMsg();
  const remove = async (e: Body) => {
    const r = await del(`${BASE}/entries/${e.id}`);
    say(r.ok ? `Tellingen på ${e.count.toLocaleString('nb-NO')} er fjernet.` : errText(r.body), r.ok ? 'ok' : 'err');
    if (r.ok) { reload(); onChanged(); }
  };
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const people: Body[] = data.people || [];
  const entries: Body[] = data.entries || [];
  return (
    <CardBody>
      <Msg text={msg.text} tone={msg.tone} />
      <h3 className="tb-h">Per person <span className="sub">(sendt med navn)</span></h3>
      {people.length ? (
        <table className="t">
          <thead><tr><th scope="col">Navn</th><th scope="col">Sum</th><th scope="col">Sendinger</th></tr></thead>
          <tbody>{people.map((p, i) => (
            <tr key={i}><td>{p.name || 'Uten navn'}</td><td><b>{Number(p.total).toLocaleString('nb-NO')}</b></td><td>{p.entries}</td></tr>))}</tbody>
        </table>
      ) : <p className="note">Ingen har sendt med navn ennå.</p>}
      {data.anonymous && data.anonymous.entries > 0 && <p className="note">Anonymt: {Number(data.anonymous.total).toLocaleString('nb-NO')} fra {plural(data.anonymous.entries, 'sending', 'sendinger')}.</p>}
      <h3 className="tb-h">Alle tellinger</h3>
      <table className="t">
        <thead><tr><th scope="col">Navn</th><th scope="col">Antall</th><th scope="col">Zikr</th><th scope="col">Tid</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
        <tbody>{entries.length ? entries.map((e) => (
          <tr key={e.id}>
            <td>{e.named ? (e.name || 'Uten navn') : <span className="sub">Anonym</span>}</td>
            <td>{Number(e.count).toLocaleString('nb-NO')}</td>
            <td>{e.phraseId ? (Dhikr.phrase(e.phraseId)?.name || e.phraseId) : '—'}</td>
            <td>{e.createdAt ? osloTime(e.createdAt) : longDate(e.day)}</td>
            <td><TwoStep label="Fjern" confirmLabel="Klikk igjen for å fjerne" busyLabel="Fjerner …" ariaLabel={`Fjern tellingen på ${e.count}`} onConfirm={() => remove(e)} /></td>
          </tr>)) : <tr><td colSpan={5}>Ingen tellinger ennå. Når noen sender fra tasbih-telleren i appen, vises det her.</td></tr>}</tbody>
      </table>
    </CardBody>
  );
}

export function Tasbih() {
  const { data, error, reload } = useLoad<Body>(`${BASE}/collections`, []);
  const [msg, say] = useMsg();
  const [form, setForm] = useState<{ c: Body | null; f: Form; start: Form } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const save = async () => {
    if (!form || saving) return;
    const local = formErrors(form.f);
    if (Object.keys(local).length) { setErrors(local); return; }
    setSaving(true);
    try {
      const r = form.c ? await put(`${BASE}/collections/${form.c.id}`, bodyOf(form.f)) : await post(`${BASE}/collections`, bodyOf(form.f));
      if (!r.ok) { setErrors(fieldErrors(r.body)); say(errSummary(r.body), 'err'); return; }
      setForm(null);
      say(form.c ? 'Innsamlingen er lagret.' : 'Innsamlingen er åpnet. Medlemmene ser den i tasbih-telleren i appen.', 'ok');
      reload();
    } finally { setSaving(false); }
  };
  const action = async (c: Body, what: 'close' | 'reopen') => {
    const r = await post(`${BASE}/collections/${c.id}/${what}`, {});
    say(r.ok ? (what === 'close' ? `«${c.title}» er avsluttet. Appen tar ikke imot flere tellinger.` : `«${c.title}» er åpen igjen.`) : errText(r.body), r.ok ? 'ok' : 'err');
    reload();
  };
  const remove = async (c: Body) => {
    const r = await del(`${BASE}/collections/${c.id}`);
    say(r.ok ? `«${c.title}» er slettet.` : errText(r.body), r.ok ? 'ok' : 'err');
    if (open === c.id) setOpen(null);
    reload();
  };

  const items: Body[] = (data && data.items) || [];
  const shown = items.find((c) => c.id === open) || null;
  return (
    <Card title="Tasbih-innsamling" id="tasbih-card"
          hint="Medlemmene sender tellingen fra tasbih-telleren i appen til en innsamling dere har åpnet — med navn eller anonymt."
          more={<>Tellingen skjer på telefonen. Bare det medlemmet selv velger å sende, kommer hit. En telling sendt med navn vises med navnet
            under «Tellinger»; en anonym telling lagres uten noe som viser hvem som sendte den. Hver gang navnene vises, føres det i aktivitetsloggen.</>}>
      <div className="work-bar">
        <span className="sp" />
        <Btn small={false} onClick={() => { setErrors({}); setForm({ c: null, f: EMPTY, start: EMPTY }); }}>+ Ny innsamling</Btn>
      </div>
      {!form && <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>}
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <>
          <p className="vh" role="status">{plural(items.length, 'innsamling', 'innsamlinger')}</p>
          <table className="t">
            <thead><tr><th scope="col">Innsamling</th><th scope="col">Fremdrift</th><th scope="col">Sendinger</th><th scope="col">Status</th>
              <th scope="col">Siste dag</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
            <tbody>{items.length ? items.map((c) => (
              <tr key={c.id}>
                <td><b>{c.title}</b><div className="sub">{phraseName(c.phraseId)}</div>{c.note && <div className="sub">{c.note}</div>}</td>
                <td>{c.target ? <div className="work-progress"><progress max={100} value={c.percent || 0} aria-label={`${c.title}: ${progressText(c)}`} /></div> : null}
                  <div className="sub">{progressText(c)}</div></td>
                <td>{c.senders}</td>
                <td>{c.open ? <Tag tone="green">Åpen</Tag> : c.closedAt ? <Tag tone="grey">Avsluttet</Tag> : <Tag tone="gold">Utløpt</Tag>}</td>
                <td>{c.endsOn ? longDate(c.endsOn) : '—'}</td>
                <td>
                  <Btn ghost aria-expanded={open === c.id} aria-label={`Tellinger for ${c.title}`} onClick={() => setOpen(open === c.id ? null : c.id)}>{open === c.id ? 'Skjul tellinger' : 'Tellinger'}</Btn>{' '}
                  <Btn ghost aria-label={`Endre ${c.title}`} onClick={() => { setErrors({}); setForm({ c, f: toForm(c), start: toForm(c) }); }}>Endre</Btn>{' '}
                  {c.closedAt
                    ? <Btn ghost aria-label={`Åpne ${c.title} igjen`} onClick={() => action(c, 'reopen')}>Åpne igjen</Btn>
                    : <TwoStep label="Avslutt" confirmLabel="Klikk igjen for å avslutte" busyLabel="Avslutter …" ariaLabel={`Avslutt ${c.title}`} onConfirm={() => action(c, 'close')} />}
                  {c.senders === 0 && <>{' '}<TwoStep label="Slett" confirmLabel="Klikk igjen for å slette" busyLabel="Sletter …" ariaLabel={`Slett ${c.title}`} onConfirm={() => remove(c)} /></>}
                </td>
              </tr>)) : <tr><td colSpan={6}>Ingen innsamlinger ennå. Åpne den første med «Ny innsamling».</td></tr>}</tbody>
          </table>
          {shown && <section aria-label={`Tellinger for ${shown.title}`}><h2 className="tb-title">Tellinger: {shown.title}</h2><Entries c={shown} onChanged={reload} /></section>}
        </>
      )}
      <FormDialog open={Boolean(form)} title={form && form.c ? 'Endre innsamling' : 'Ny innsamling'} dirty={Boolean(form) && JSON.stringify(form!.f) !== JSON.stringify(form!.start)}
        onClose={() => setForm(null)}>
        {form && <>
          <CollectionForm f={form.f} set={(f) => { setErrors(dropChanged(errors, form.f, f)); setForm({ ...form, f }); }} errors={errors}
            phraseLocked={Boolean(form.c && form.c.senders > 0)} />
          <div className="row"><Btn small={false} disabled={saving} onClick={save}>{saving ? 'Lagrer …' : form.c ? 'Lagre' : 'Åpne innsamlingen'}</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
    </Card>
  );
}
